import { Col, Row } from "antd";
import { useFormik } from "formik";
import { get, isEmpty } from "lodash";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";
import format from "string-template";
import { downloadUploadedDocument } from "utils/helpers/files";
import ActionButtons from "../Components/ActionButtons";
import Header from "../Components/Header";
import Layout from "../Components/Layout";
import DocumentUpload from "../Justification/DocumentUpload";
import validation from "./validation";

function View({
  initialValues,
  onSubmit,
  downloadFile,
  onBack,
  translate,
  onFileRemove: handleFileRemove,
}) {
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const { isValid, handleSubmit, setFieldValue, values, ...formProps } =
    useFormik({
      initialValues,
      enableReinitialize: true,
      onSubmit: onSubmit,
      validationSchema: validation(
        t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
        get(initialValues, "includedChildren", []),
      ),
    });

  const includedChildren = get(values, "includedChildren", []);

  if (!includedChildren || !Array.isArray(includedChildren)) {
    return (
      <>{t("B2C_AMELLIS.CHILDREN_JUSTIFICATIONS.LOADING", "Chargement...")}</>
    );
  }

  const createFileRemoveHandler = (fieldName) => async (item) => {
    if (handleFileRemove) {
      await handleFileRemove(fieldName)(item);
      const currentFiles = get(values, fieldName, []) || [];
      const updatedFiles = currentFiles.filter(
        (file) => (file?.uid || file?.id) !== (item?.uid || item?.id),
      );
      setFieldValue(fieldName, updatedFiles);
    }
  };

  const handleDownload = ({ uid, size, base64, contentType, name }) => {
    if (!size) return downloadFile(uid);
    downloadUploadedDocument({
      base64,
      contentType,
      name,
    });
  };

  return (
    <Layout>
      <Header
        title={"Pièces justificatives des enfants"}
        highlight="Pièces justificatives"
        subtitle="Pièces justificatives"
      />
      <Row justify="center">
        <Col xs={24} md={16} lg={14} xl={12}>
          <div className="amellis__documents--container">
            {includedChildren.map((child, index) => {
              let title;
              if (index === 0) {
                title = translate?.CHILD_TITLES?.FIRST || "Premier enfant";
              } else if (index === 1) {
                title = translate?.CHILD_TITLES?.SECOND || "Deuxième enfant";
              } else if (index === 2) {
                title = translate?.CHILD_TITLES?.THIRD || "Troisième enfant";
              } else {
                title = translate?.CHILD_TITLES?.NTH
                  ? format(translate.CHILD_TITLES.NTH, { n: index + 1 })
                  : `${index + 1}ème enfant`;
              }

              // Get child's name
              const firstName = get(child, "identity.user.firstname", "");
              const lastName = get(child, "identity.user.lastname", "");
              const childName = [firstName, lastName].filter(Boolean).join(" ");
              const titleWithName = childName
                ? `${title} - ${childName}`
                : title;

              // Determine document type and field key based on age
              const isUnder18 = child.age < 18;
              const documentKey = isUnder18
                ? `socialSecurityCertificate_${child.userId}`
                : `studentEnrollmentCertificate_${child.userId}`;

              const documentLabel = isUnder18
                ? translate?.DOCUMENTS?.TYPES?.SOCIAL_SECURITY_CERTIFICATE ||
                  "Attestation de sécurité sociale"
                : translate?.DOCUMENTS?.TYPES?.STUDENT_ENROLLMENT_CERTIFICATE ||
                  "Attestation de scolarité";

              return (
                <div
                  key={child.userId}
                  className="amellis__children-justification__child-section"
                >
                  <h3 className="amellis__children-justification__child-section--title">
                    {titleWithName}
                  </h3>
                  <DocumentUpload
                    label={documentLabel}
                    items={get(values, documentKey, [])}
                    onDownload={handleDownload}
                    onUpload={(item) => {
                      setFieldValue(documentKey, item);
                    }}
                    onFileRemove={createFileRemoveHandler(documentKey)}
                    required={true}
                  />
                  {get(formProps.errors, documentKey) && (
                    <span className="amellis__children-justification__error-message">
                      {get(formProps.errors, documentKey)}
                    </span>
                  )}
                </div>
              );
            })}
            <div className="recap__wrapper--info">
              <p>
                {translate?.DOCUMENTS?.ALLOWED_FILES ||
                  "Formats de fichiers pris en charge : JPEG, PNG ou PDF"}
              </p>
              <p>
                {translate?.DOCUMENTS?.MAX_SIZE || "Taille maximale : 5 Mo"}
              </p>
            </div>
          </div>
        </Col>
      </Row>
      <ActionButtons
        disabled={!isEmpty(formProps.errors)}
        onNext={handleSubmit}
        onBack={onBack}
        buttonText={translate?.NEXT || "Suivant"}
      />
    </Layout>
  );
}

export default View;
